import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { IconButton, Tooltip } from '@mui/material';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { createCheckoutSession, getPublicPromotion, signupFoodstation } from '../../services/api';
import { markInitialSetupPending } from '../../services/initialSetup';
import { getApiErrorMessage } from '../../utils/apiError';
import { acceptLogin } from '../../services/browserAuthRuntime';
import { EMAIL_RECOVERY_HELP, isValidUserEmail } from '../../utils/userEmail';
import {
  FOODSTATION_TRIAL_PRODUCTS,
  getProductSourceFromReferrer,
  getProductSourceFromSearch,
  orderProductsBySource,
} from '../../utils/productDisplayCatalog';
import logo from '../FoodstationLP/foodstation.png';
import './RegisterPage.css';

const PLAN_KEYS = new Set(['essencial', 'pro', 'enterprise']);

const onlyDigits = (value) => value.replace(/\D/g, '');

const getPlanFromSearch = (search) => {
  const params = new URLSearchParams(search || '');
  const plan = (params.get('plan') || '').trim().toLowerCase();
  return PLAN_KEYS.has(plan) ? plan : null;
};

const getTestInviteFromSearch = (search) => {
  const params = new URLSearchParams(search || '');
  const inviteCode =
    params.get('invite')
    || params.get('test_invite')
    || params.get('convite_teste')
    || '';
  return inviteCode.trim();
};

const getOfferTokenFromSearch = (search) => {
  const params = new URLSearchParams(search || '');
  return (params.get('oferta') || params.get('offer') || params.get('promotion') || '').trim();
};

const OFFER_PRODUCT_LABELS = {
  atendabot: 'AtendaBot',
  reservo: 'Reservo',
  one_menu: 'One Menu',
  feedback_hero: 'Feedback Hero',
  curriculo_certo: 'Curriculo Certo',
};

const formatOfferMoney = (cents) => new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
}).format((Number(cents) || 0) / 100);

const formatWhatsapp = (value) => {
  const digits = onlyDigits(value).slice(0, 11);

  if (digits.length <= 2) {
    return digits;
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  }
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const validateForm = (values) => {
  const errors = {};
  const name = values.name?.trim() || '';
  const restaurantName = values.restaurantName?.trim() || '';
  const email = values.email?.trim() || '';
  const whatsappDigits = onlyDigits(values.whatsapp || '');

  if (!name) {
    errors.name = 'Informe seu nome.';
  } else if (name.length < 2) {
    errors.name = 'Nome muito curto.';
  }

  if (!email) {
    errors.email = 'Informe seu email.';
  } else if (!isValidUserEmail(email)) {
    errors.email = 'Email inválido.';
  }

  if (!whatsappDigits) {
    errors.whatsapp = 'Informe seu WhatsApp.';
  } else if (![10, 11].includes(whatsappDigits.length)) {
    errors.whatsapp = 'WhatsApp inválido.';
  }

  if (!restaurantName) {
    errors.restaurantName = 'Informe o nome do restaurante.';
  } else if (restaurantName.length < 2) {
    errors.restaurantName = 'Nome do restaurante muito curto.';
  }

  if (!values.password) {
    errors.password = 'Informe uma senha.';
  } else if (values.password.length < 6) {
    errors.password = 'A senha deve ter ao menos 6 caracteres.';
  }

  if (!values.confirmPassword) {
    errors.confirmPassword = 'Confirme sua senha.';
  } else if (values.password !== values.confirmPassword) {
    errors.confirmPassword = 'As senhas não conferem.';
  }

  return errors;
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const planKey = getPlanFromSearch(location.search);
  const testInviteCode = getTestInviteFromSearch(location.search);
  const offerToken = getOfferTokenFromSearch(location.search);
  const isPromotionSignup = Boolean(offerToken);
  const sourceProductKey = useMemo(() => {
    const sourceFromSearch = getProductSourceFromSearch(location.search);
    if (sourceFromSearch) return sourceFromSearch;
    if (typeof document === 'undefined') return null;
    return getProductSourceFromReferrer(document.referrer);
  }, [location.search]);
  const signupProducts = useMemo(
    () => orderProductsBySource(FOODSTATION_TRIAL_PRODUCTS, sourceProductKey),
    [sourceProductKey]
  );
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsapp: '',
    restaurantName: '',
    password: '',
    confirmPassword: '',
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [promotion, setPromotion] = useState(null);
  const [promotionError, setPromotionError] = useState('');
  const [isPromotionLoading, setIsPromotionLoading] = useState(Boolean(offerToken));

  useEffect(() => {
    let active = true;
    if (!offerToken) {
      setPromotion(null);
      setPromotionError('');
      setIsPromotionLoading(false);
      return () => { active = false; };
    }
    setIsPromotionLoading(true);
    getPublicPromotion(offerToken)
      .then((response) => {
        if (!active) return;
        setPromotion(response?.data || null);
        setPromotionError('');
      })
      .catch((error) => {
        if (!active) return;
        setPromotion(null);
        setPromotionError(getApiErrorMessage(error, 'Esta oferta não está mais disponível.'));
      })
      .finally(() => { if (active) setIsPromotionLoading(false); });
    return () => { active = false; };
  }, [offerToken]);

  const handleChange = (field) => (event) => {
    const value = field === 'whatsapp'
      ? formatWhatsapp(event.target.value)
      : event.target.value;

    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrorMessage('');
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleBlur = (field) => () => {
    const nextErrors = validateForm(formData);
    setErrors((prev) => ({ ...prev, [field]: nextErrors[field] }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      const message = 'Verifique os campos destacados.';
      setErrorMessage(message);
      toast.error(message);
      return;
    }
    if (offerToken && (isPromotionLoading || promotionError || !promotion)) {
      const message = promotionError || 'Aguarde a validacao da oferta.';
      setErrorMessage(message);
      toast.error(message);
      return;
    }

    setIsSubmitting(true);

    try {
      const normalizedEmail = formData.email.trim().toLowerCase();
      const signupPayload = {
        name: formData.name,
        email: normalizedEmail,
        whatsapp: formData.whatsapp,
        restaurantName: formData.restaurantName,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
      };
      if (testInviteCode) {
        signupPayload.testInviteCode = testInviteCode;
      }
      if (offerToken) {
        signupPayload.offerToken = offerToken;
      }
      const response = await signupFoodstation(signupPayload);

      if (!response.data?.session) throw new Error('Sessão não retornada.');
      acceptLogin(response.data);
      markInitialSetupPending(response.data.session);

      const skipCheckoutForTestInvite = Boolean(
        response?.data?.session?.billing?.billingExempt
      );

      if (skipCheckoutForTestInvite) {
        toast.success('Conta criada com sucesso. Checkout dispensado.');
        navigate('/setup-inicial', { replace: true });
        return;
      }

      const stripePayload = { email: normalizedEmail };
      if (planKey) {
        stripePayload.plan = planKey;
      } else if (sourceProductKey) {
        stripePayload.products = [sourceProductKey];
        stripePayload.trialPeriodDays = 7;
      }
      if (offerToken) {
        delete stripePayload.plan;
        delete stripePayload.products;
        delete stripePayload.trialPeriodDays;
        stripePayload.offerToken = offerToken;
      }

      try {
        const stripeResponse = await createCheckoutSession(stripePayload);
        const url = stripeResponse?.data?.url;
        if (url) {
          window.location.href = url;
          return;
        }

        throw new Error('Não foi possível iniciar o checkout.');
      } catch (stripeError) {
        const status = Number(stripeError?.response?.status);
        if (!stripeError?.response || status >= 500) {
          toast.warn(
            'Sua conta foi criada, mas não conseguimos abrir o pagamento. Tente novamente em Pagamentos.'
          );
          navigate('/billing', { replace: true });
          return;
        }
        const message = getApiErrorMessage(
          stripeError,
          'Não foi possível iniciar o pagamento. Tente novamente.'
        );
        setErrorMessage(message);
        toast.error(message);
        return;
      }
    } catch (error) {
      const message = getApiErrorMessage(
        error,
        'Não foi possível criar sua conta. Verifique sua conexão e tente novamente.'
      );
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = isPromotionSignup
    ? [
      'Oferta promocional aplicada ao cadastro',
      'Ativação após a confirmação do pagamento',
      'Sem burocracia para começar',
      'Produtos da promoção liberados após o pagamento',
    ]
    : [
      '7 dias grátis para testar',
      'Ativação instantânea após o cadastro',
      'Sem burocracia para começar',
      'Todos os sistemas liberados no teste grátis',
    ];
  const submitButtonLabel = isSubmitting
    ? (isPromotionSignup ? 'Criando conta…' : 'Iniciando teste…')
    : (isPromotionSignup ? 'Continuar para pagamento' : 'Iniciar teste grátis');

  const CheckIcon = () => (
    <svg
      className="signup-benefit-icon"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );

  const SparkIcon = () => (
    <svg
      className="signup-pill-icon"
      viewBox="0 0 24 24"
      width="13"
      height="13"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 2l1.6 5.4L19 9l-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6L12 2zm6 12l.9 2.6 2.6.9-2.6.9L18 21l-.9-2.6-2.6-.9 2.6-.9L18 14z" />
    </svg>
  );

  return (
    <div className="signup-page">
      <header className="signup-header">
        <button type="button" className="signup-back" onClick={() => navigate('/')}>
          Voltar
        </button>
        <img src={logo} alt="Foodstation" className="signup-logo" />
        <button type="button" className="signup-login" onClick={() => navigate('/login')}>
          Login
        </button>
      </header>

      <div className="signup-shell">
        <aside className="signup-info">
          <span className="signup-pill">
            <SparkIcon />
            {isPromotionSignup ? 'Oferta especial' : '7 dias grátis'}
          </span>
          <h1>
            {isPromotionSignup
              ? 'Crie sua conta com a oferta especial'
              : 'Comece seu teste gratuito agora'}
          </h1>
          <p>
            {isPromotionSignup
              ? 'Preencha seus dados e continue para o pagamento da sua oferta FoodStation.'
              : 'Crie sua conta em menos de 1 minuto e comece a usar o FoodStation imediatamente.'}
          </p>
          <ul className="signup-highlights">
            {benefits.map((text) => (
              <li key={text}>
                <CheckIcon />
                <span>{text}</span>
              </li>
            ))}
          </ul>
          <div
            className="signup-products"
            aria-label={isPromotionSignup ? 'Sistemas FoodStation' : 'Sistemas liberados no teste grátis'}
          >
            {signupProducts.map((product) => (
              <div
                key={product.key}
                className={`signup-product-card${product.key === sourceProductKey ? ' is-source' : ''}`}
              >
                <span
                  className="signup-product-mark"
                  style={{
                    '--signup-product-bg': product.backgroundColor,
                    '--signup-product-logo-size': product.logoSize,
                  }}
                  aria-hidden="true"
                >
                  <img
                    className="signup-product-logo"
                    src={product.image}
                    alt=""
                    loading="lazy"
                  />
                </span>
                <span className="signup-product-name">{product.name}</span>
              </div>
            ))}
          </div>
        </aside>

        <form className="signup-card" onSubmit={handleSubmit} noValidate>
          <div className="signup-card-head">
            <h2>Crie sua conta</h2>
            <p>
              {isPromotionSignup
                ? 'Preencha seus dados para continuar para o pagamento.'
                : 'Preencha seus dados para iniciar o teste gratuito.'}
            </p>
          </div>
          {isPromotionLoading && (
            <div className="signup-offer-banner">Validando sua oferta especial...</div>
          )}
          {promotionError && (
            <div className="signup-offer-banner is-error">{promotionError}</div>
          )}
          {promotion && (
            <div className="signup-offer-banner">
              <strong>{promotion.name}</strong>
              <span>Codigo {promotion.code}</span>
              <ul>
                {(promotion.products || []).map((product) => (
                  <li key={product.product_key}>
                    {OFFER_PRODUCT_LABELS[product.product_key] || product.product_key}: {' '}
                    {product.product_key === 'one_menu'
                      && promotion.products.some((entry) => entry.product_key === 'atendabot')
                      ? 'Incluso no AtendaBot'
                      : promotion.pricing_mode === 'fixed_price'
                      ? `${formatOfferMoney(product.promotional_amount_cents)}/mes`
                      : `${promotion.percent_off}% de desconto`}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="signup-field">
            <label className="signup-label" htmlFor="signup-name">
              Nome
            </label>
            <input
              id="signup-name"
              type="text"
              value={formData.name}
              onChange={handleChange('name')}
              onBlur={handleBlur('name')}
              placeholder="Seu nome completo"
              autoComplete="name"
              minLength={2}
              required
              className={errors.name ? 'is-invalid' : ''}
            />
            {errors.name && <span className="signup-field-error">{errors.name}</span>}
          </div>

          <div className="signup-field">
            <div className="signup-label-row">
              <label className="signup-label" htmlFor="signup-email">E-mail</label>
              <Tooltip title={EMAIL_RECOVERY_HELP} enterTouchDelay={0} describeChild>
                <IconButton type="button" size="small" aria-label="Sobre o e-mail e a recuperação de senha" sx={{ p: 0.25 }}>
                  <HelpOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </div>
            <input
              id="signup-email"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              onBlur={handleBlur('email')}
              placeholder="Seu melhor email"
              autoComplete="email"
              maxLength={254}
              required
              className={errors.email ? 'is-invalid' : ''}
            />
            {errors.email && <span className="signup-field-error">{errors.email}</span>}
          </div>

          <div className="signup-field">
            <label className="signup-label" htmlFor="signup-whatsapp">
              WhatsApp
            </label>
            <input
              id="signup-whatsapp"
              type="tel"
              value={formData.whatsapp}
              onChange={handleChange('whatsapp')}
              onBlur={handleBlur('whatsapp')}
              placeholder="Número com DDD"
              autoComplete="tel"
              inputMode="numeric"
              required
              className={errors.whatsapp ? 'is-invalid' : ''}
            />
            {errors.whatsapp && <span className="signup-field-error">{errors.whatsapp}</span>}
          </div>

          <div className="signup-field">
            <label className="signup-label" htmlFor="signup-restaurant">
              Nome do restaurante
            </label>
            <input
              id="signup-restaurant"
              type="text"
              value={formData.restaurantName}
              onChange={handleChange('restaurantName')}
              onBlur={handleBlur('restaurantName')}
              placeholder="Nome do seu restaurante"
              autoComplete="organization"
              minLength={2}
              required
              className={errors.restaurantName ? 'is-invalid' : ''}
            />
            {errors.restaurantName && (
              <span className="signup-field-error">{errors.restaurantName}</span>
            )}
          </div>

          <div className="signup-field-row">
            <div className="signup-field">
              <label className="signup-label" htmlFor="signup-password">
                Senha
              </label>
              <input
                id="signup-password"
                type="password"
                value={formData.password}
                onChange={handleChange('password')}
                onBlur={handleBlur('password')}
                placeholder="Crie uma senha de acesso"
                autoComplete="new-password"
                minLength={6}
                required
                className={errors.password ? 'is-invalid' : ''}
              />
              {errors.password && <span className="signup-field-error">{errors.password}</span>}
            </div>

            <div className="signup-field">
              <label className="signup-label" htmlFor="signup-confirm">
                Repetir senha
              </label>
              <input
                id="signup-confirm"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange('confirmPassword')}
                onBlur={handleBlur('confirmPassword')}
                placeholder="Repita sua senha"
                autoComplete="new-password"
                minLength={6}
                required
                className={errors.confirmPassword ? 'is-invalid' : ''}
              />
              {errors.confirmPassword && (
                <span className="signup-field-error">{errors.confirmPassword}</span>
              )}
            </div>
          </div>

          {errorMessage && <p className="signup-error">{errorMessage}</p>}

          <button type="submit" className="signup-submit" disabled={isSubmitting}>
            {submitButtonLabel}
          </button>

          <p className="signup-footnote">
            {isPromotionSignup
              ? 'Ao criar sua conta, você será direcionado ao pagamento seguro da sua oferta.'
              : 'Ao criar sua conta, seu teste gratuito será ativado automaticamente e você poderá começar a usar o FoodStation.'}
          </p>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
